import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import {
  DeleteVideoDto,
  RenameVideoDto,
  VideoDto,
  VideoSort,
} from "@repo/api/video";
import { DuplicateVideoResponseDto } from "@repo/api/editor";
import { VideoApi } from "../api/video-api";
import { EditorApi } from "../api/editor-api";
import { queryClient } from "./query-client";
import { folderQueryKeys } from "./folder-queries";
import { homeQueryKeys } from "./home-queries";
import Toast from "../components/Toast";

export function usePagedVideosQuery(
  teamExternalId: string,
  sort: VideoSort = "lastUpdated",
) {
  return useInfiniteQuery({
    queryKey: videoQueryKeys.paged(teamExternalId, sort),
    queryFn: ({ pageParam }) =>
      VideoApi.getPagedVideos(teamExternalId, { page: pageParam, sort }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 0,
  });
}

export function useSearchVideosQuery(teamExternalId: string, search: string) {
  return useQuery({
    queryKey: videoQueryKeys.search(teamExternalId, search),
    queryFn: () => VideoApi.searchVideos(teamExternalId, { search }),
    enabled: search.length > 0,
  });
}

async function invalidateVideosAndFolders(teamExternalId: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: videoQueryKeys.all() }),
    queryClient.invalidateQueries({
      queryKey: folderQueryKeys.all(teamExternalId),
    }),
    queryClient.invalidateQueries({
      queryKey: homeQueryKeys.home(teamExternalId),
    }),
  ]);
}

export function useRenameVideoMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: (data: VideoDto) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationFn: ({
      videoExternalId,
      data,
    }: {
      videoExternalId: string;
      data: RenameVideoDto;
    }) => VideoApi.renameVideo(teamExternalId, videoExternalId, data),
    onSuccess: async (data) => {
      await invalidateVideosAndFolders(teamExternalId);
      onSuccess?.(data);
    },
    onError: (error) => {
      Toast.error("Failed to rename video");
      onError?.(error);
    },
  });
}

export function useDeleteVideoMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: (data: DeleteVideoDto) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationFn: (videoExternalId: string) =>
      EditorApi.deleteVideo(teamExternalId, videoExternalId),
    onSuccess: async (data) => {
      await invalidateVideosAndFolders(teamExternalId);
      onSuccess?.(data);
    },
    onError: (error) => {
      Toast.error("Failed to delete video");
      onError?.(error);
    },
  });
}

export function useDuplicateVideoMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: (data: DuplicateVideoResponseDto) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationFn: (videoExternalId: string) =>
      EditorApi.duplicateVideo(teamExternalId, videoExternalId),
    onSuccess: async (data) => {
      await invalidateVideosAndFolders(teamExternalId);
      onSuccess?.(data);
    },
    onError: (error) => {
      Toast.error("Failed to duplicate video");
      onError?.(error);
    },
  });
}

export const videoQueryKeys = {
  all: () => ["video"],
  paged: (teamExternalId: string, sort: VideoSort) => [
    "video",
    "paged",
    { teamExternalId, sort },
  ],
  search: (teamExternalId: string, search: string) => [
    "video",
    "search",
    { teamExternalId, search },
  ],
};
